import React, { createContext, useContext, useEffect, useState } from 'react';
import { logger } from '../utils/logger';

/**
 * Viewport context value type
 */
export interface ViewportContextType {
  /** Whether the current viewport is mobile-sized (max-width: 1024px) */
  isMobile: boolean;
  /** Whether the current viewport is small desktop (1025px - 1365px) */
  isSmallDesktop: boolean;
  /** Whether the current viewport is large desktop (min-width: 1366px) */
  isLargeDesktop: boolean;
}

// Context with default desktop values
const ViewportContext = createContext<ViewportContextType>({
  isMobile: false,
  isSmallDesktop: false,
  isLargeDesktop: true,
});

/**
 * Hook for detecting and responding to viewport changes
 * Uses media query to determine viewport sizes
 */
export function useViewportMediaQuery(): ViewportContextType {
  const [isMobile, setIsMobile] = useState<boolean>(
    () => window.matchMedia('(max-width: 1024px)').matches
  );
  const [isSmallDesktop, setIsSmallDesktop] = useState<boolean>(
    () => window.matchMedia('(min-width: 1025px) and (max-width: 1365px)').matches
  );
  const [isLargeDesktop, setIsLargeDesktop] = useState<boolean>(
    () => window.matchMedia('(min-width: 1366px)').matches
  );


  useEffect(() => {
    const mobileQuery = window.matchMedia('(max-width: 1024px)');
    const smallDesktopQuery = window.matchMedia('(min-width: 1025px) and (max-width: 1365px)');
    const largeDesktopQuery = window.matchMedia('(min-width: 1366px)');

    // Add a small debounce for rapid changes like dev tools toggling
    let debounceTimeout: number | undefined;

    const handleViewportChange = (): void => {
      // Clear existing timeout
      if (debounceTimeout) {
        window.clearTimeout(debounceTimeout);
      }

      // Set a small debounce to prevent multiple rapid updates
      debounceTimeout = window.setTimeout(() => {
        logger.viewport('Viewport changed', {
          isMobile: mobileQuery.matches,
          isSmallDesktop: smallDesktopQuery.matches,
          isLargeDesktop: largeDesktopQuery.matches
        });

        // Batch these updates together
        setIsMobile(mobileQuery.matches);
        setIsSmallDesktop(smallDesktopQuery.matches);
        setIsLargeDesktop(largeDesktopQuery.matches);
      }, 50); // Short delay to batch updates
    };

    // Use a single handler for all viewport changes
    mobileQuery.addEventListener('change', handleViewportChange);
    smallDesktopQuery.addEventListener('change', handleViewportChange);
    largeDesktopQuery.addEventListener('change', handleViewportChange);

    // Initial check
    handleViewportChange();

    return () => {
      // Clean up event listeners
      mobileQuery.removeEventListener('change', handleViewportChange);
      smallDesktopQuery.removeEventListener('change', handleViewportChange);
      largeDesktopQuery.removeEventListener('change', handleViewportChange);

      // Clear any pending timeout
      if (debounceTimeout) {
        window.clearTimeout(debounceTimeout);
      }
    };
  }, []); // No dependencies needed for this effect

  return { isMobile, isSmallDesktop, isLargeDesktop };
}

/**
 * Provider component that makes viewport information available throughout the app
 * @param children - React children components
 */
export function ViewportProvider({ children }: { children: React.ReactNode }) {
  const viewport = useViewportMediaQuery();

  return <ViewportContext.Provider value={viewport}>{children}</ViewportContext.Provider>;
}

/* eslint-disable react-refresh/only-export-components */
/**
 * Hook for accessing viewport information throughout the app
 * This is the recommended way to check for mobile/desktop viewport across the application
 * @returns Object containing the current viewport state
 */
export const useViewport = () => useContext(ViewportContext);
/* eslint-enable react-refresh/only-export-components */
